/**
 * Calculadora de rentabilidad para Shopify
 * Lee los productos del carrito (/cart.js) y calcula, según el % de ganancia
 * que ingresa el cliente: inversión, venta potencial, ganancia, margen y rentabilidad.
 *
 * Modos: "inline" (section en la página) y "flotante" (botón fijo + modal).
 *
 * Definiciones:
 *   Inversión   = lo que pagas por las joyas, con el descuento del tramo si aplica (+ costos variables)
 *   Venta       = Σ precio a pagar de cada línea × (1 + % de esa línea)
 *   Ganancia    = Venta − Inversión
 *   Margen      = Ganancia / Venta        (qué parte de cada venta es ganancia)
 *   Rentabilidad= Ganancia / Inversión    (retorno sobre lo invertido, ROI)
 */
class CalculadoraRentabilidad extends HTMLElement {
  connectedCallback() {
    this.defaultPct = parseFloat(this.dataset.defaultMarkup) || 50;
    this.porItem = this.dataset.perItem === 'true';
    this.neto = this.dataset.neto === 'true';
    this.iva = parseFloat(this.dataset.iva) || 19;
    this.locale = this.dataset.locale || 'es-CL';
    this.saleLabel = this.dataset.saleLabel || 'Oferta';
    this.modo = this.dataset.modo || 'inline';
    this.ocultarVacio = this.dataset.ocultarVacio === 'true';
    this.overrides = {};
    this.ventasManual = {};
    this.markupMode = [30, 50, 100].includes(this.defaultPct) ? String(this.defaultPct) : 'manual';
    this.sim = 100000;
    this.cart = null;
    this.compareByVariant = {};

    this.globalInput = this.querySelector('[data-global-pct]');
    this.costosInputs = this.querySelectorAll('[data-costos]');
    this.itemsEl = this.querySelector('[data-items]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.bodyEl = this.querySelector('[data-body]');

    this.restaurarEstado();
    this.cantidades = {};
    this.addEventListener('input', (e) => this.onInput(e));
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-actualizar-carrito]')) {
        this.actualizarCarrito(e.target.closest('[data-actualizar-carrito]'));
        return;
      }
      if (e.target.closest('[data-revert]')) {
        this.cantidades = {};
        this.render();
        if (this.modal) this.cerrar();
        return;
      }
      const modo = e.target.closest('[data-mode]');
      if (modo) {
        if (modo.dataset.mode === 'manual') {
          if (this.markupMode !== 'manual') this.congelarVentas();
          this.markupMode = 'manual';
        } else {
          this.markupMode = modo.dataset.mode;
          this.defaultPct = Number(this.markupMode);
          this.overrides = {};
          this.ventasManual = {};
        }
        this.renderLineas(true);
        this.renderTotales();
        this.guardarEstado();
        return;
      }
      const sim = e.target.closest('[data-sim]');
      if (sim) {
        this.sim = Number(sim.dataset.sim) || 0;
        this.renderVacio();
        return;
      }
      const verCarrito = e.target.closest('[data-ver-carrito]');
      if (verCarrito) {
        if (this.modal && (this.modal.open || this.modal.hasAttribute('open'))) this.cerrarYa();
        document.querySelector('cart-drawer')?.open(verCarrito);
        return;
      }
      const paso = e.target.closest('[data-qty-step]');
      if (!paso) return;
      const input = paso.parentElement.querySelector('[data-item-qty]');
      if (!input) return;
      const siguiente = Math.max(0, (parseInt(input.value, 10) || 0) + Number(paso.dataset.qtyStep));
      input.value = String(siguiente);
      this.fijarCantidad(input.dataset.itemQty, siguiente);
    });

    if (this.modo === 'flotante') this.initModal();
    if (!window.__calcOpenBound) {
      window.__calcOpenBound = true;
      document.addEventListener('click', (event) => {
        if (!event.target.closest('[data-open-calc]')) return;
        event.preventDefault();
        const calc = document.querySelector('calculadora-rentabilidad[data-modo="flotante"]') || document.querySelector('calculadora-rentabilidad');
        if (calc?.abrir) calc.abrir();
        else document.getElementById('calculadora')?.scrollIntoView({ behavior: 'smooth' });
      });
    }

    this.escucharCarrito();
    this.refresh();
  }

  initModal() {
    this.modal = this.querySelector('[data-modal]');
    this.fab = this.querySelector('[data-abrir]');
    this.fabValor = this.querySelector('[data-fab-valor]');

    this.fab.addEventListener('click', () => this.abrir());
    this.alinearConWhatsapp();
    this.querySelectorAll('[data-cerrar]').forEach((b) => b.addEventListener('click', () => this.cerrar()));

    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.cerrar();
    });
    this.modal.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.cerrar();
    });
    this.modal.addEventListener('close', () => document.body.classList.remove('calc-rent-open'));
  }

  alinearConWhatsapp() {
    if (!this.fab) return;
    const gap = 12;
    const esMobile = () => window.matchMedia('(max-width: 767px)').matches;
    const actualizar = () => {
      this.fab.style.removeProperty('--mj-wa-bottom');
      const barra = document.querySelector('[id^="sticky-add-to-cart-"]');
      if (!esMobile() || !barra || getComputedStyle(barra).display === 'none') return;
      const alto = barra.getBoundingClientRect().height;
      if (alto <= 0) return;
      this.fab.style.setProperty('--mj-wa-bottom', `${Math.round(alto + gap)}px`);
    };
    const barra = document.querySelector('[id^="sticky-add-to-cart-"]');
    if (barra) {
      new MutationObserver(actualizar).observe(barra, { attributes: true, attributeFilter: ['style', 'class'] });
      if (typeof ResizeObserver !== 'undefined') new ResizeObserver(actualizar).observe(barra);
    }
    window.addEventListener('resize', actualizar, { passive: true });
    actualizar();
  }

  abrir() {
    if (this.modal.open || this.modal.hasAttribute('open')) return;
    if (typeof this.modal.showModal === 'function') this.modal.showModal();
    else this.modal.setAttribute('open', '');
    document.body.classList.add('calc-rent-open');
    const activo = this.modal.querySelector(':focus');
    if (activo && activo !== this.modal) activo.blur();
    this.modal.focus({ preventScroll: true });
    this.refresh();
  }

  cerrar() {
    if (this._cerrando) return;
    const abierto = this.modal.open || this.modal.hasAttribute('open');
    if (!abierto) return;

    const reducir = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducir) {
      this.cerrarYa();
      return;
    }

    this._cerrando = true;
    this.modal.classList.add('is-closing');
    const terminar = (e) => {
      if (e && e.target !== this.modal) return;
      if (!this._cerrando) return;
      this.modal.removeEventListener('animationend', terminar);
      this.cerrarYa();
    };
    this.modal.addEventListener('animationend', terminar);
    this._cerrarTimer = setTimeout(terminar, 280);
  }

  cerrarYa() {
    clearTimeout(this._cerrarTimer);
    this._cerrando = false;
    this.modal.classList.remove('is-closing');
    if (typeof this.modal.close === 'function') {
      if (this.modal.open) this.modal.close();
    } else {
      this.modal.removeAttribute('open');
    }
    document.body.classList.remove('calc-rent-open');
  }

  guardarEstado() {
    try {
      sessionStorage.setItem(
        'calcRent',
        JSON.stringify({
          g: this.pctGlobal(),
          c: this.costosValores(),
          o: this.overrides,
          d: this.defaultPct,
          m: this.markupMode,
          v: this.ventasManual,
        })
      );
    } catch (_) {}
  }

  restaurarEstado() {
    let prev = null;
    try {
      prev = JSON.parse(sessionStorage.getItem('calcRent') || 'null');
    } catch (_) {}
    if (this.globalInput) this.globalInput.value = this.defaultPct;
    if (prev?.c != null) {
      if (typeof prev.c === 'object') {
        this.costosInputs.forEach((el) => {
          const val = prev.c[el.dataset.costos];
          if (val != null) el.value = val;
        });
        const pack = this.querySelector('[data-costos="packaging"]');
        if (pack && prev.c.packaging == null && (prev.c.empaque != null || prev.c.cajas != null)) {
          pack.value = (parseFloat(prev.c.empaque) || 0) + (parseFloat(prev.c.cajas) || 0);
        }
      } else {
        const otros = this.querySelector('[data-costos="otros"]');
        if (otros) otros.value = prev.c;
      }
    }
    if (prev?.o && typeof prev.o === 'object') this.overrides = prev.o;
    if (prev?.m === 'manual' || prev?.m === '30' || prev?.m === '50' || prev?.m === '100') this.markupMode = prev.m;
    if (this.markupMode === '50' && !this.querySelector('[data-mode="50"]') && this.querySelector('[data-mode="100"]')) {
      this.markupMode = '100';
      this.defaultPct = 100;
    }
    if (prev?.v && typeof prev.v === 'object') this.ventasManual = prev.v;
    if (this.markupMode !== 'manual') this.ventasManual = {};
  }

  costosValores() {
    const vals = {};
    this.costosInputs.forEach((el) => {
      vals[el.dataset.costos] = el.value;
    });
    return vals;
  }

  costosExtra() {
    let total = 0;
    this.costosInputs.forEach((el) => {
      total += Math.max(0, parseFloat(el.value) || 0);
    });
    return total;
  }

  escucharCarrito() {
    if (!window.__calcRentFetchPatched) {
      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const res = await originalFetch(...args);
        try {
          const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
          if (/\/cart\/(add|change|update|clear)/.test(url)) {
            document.dispatchEvent(new CustomEvent('calc-rent:cart-changed'));
          }
        } catch (_) {}
        return res;
      };
      window.__calcRentFetchPatched = true;
    }

    if (!window.__calcRentXhrPatched) {
      const open = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.addEventListener('load', () => {
          if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
            document.dispatchEvent(new CustomEvent('calc-rent:cart-changed'));
          }
        });
        return open.call(this, method, url, ...rest);
      };
      window.__calcRentXhrPatched = true;
    }

    document.addEventListener('calc-rent:cart-changed', () => this.refreshDebounced());

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => this.refreshDebounced());
    }
  }

  refreshDebounced() {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.refresh(), 250);
  }

  async refresh() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      this.cart = await res.json();
      this.cantidades = {};
      await this.cargarCompareAt(root);
      this.render();
    } catch (err) {
      console.error('[Calculadora rentabilidad] No se pudo leer el carrito', err);
    }
  }

  itemsFacturables() {
    return (this.cart?.items || []).filter((item) => {
      const linea = Number(item.final_line_price ?? item.line_price ?? 0);
      const unitario = Number(item.final_price ?? item.price ?? 0);
      return linea > 0 && unitario > 0;
    });
  }

  async cargarCompareAt(root) {
    const items = this.itemsFacturables();
    const missing = items.filter((item) => this.compareByVariant[item.variant_id] == null);
    const handles = [...new Set(missing.map((item) => item.handle).filter(Boolean))];
    await Promise.all(
      handles.map(async (handle) => {
        try {
          const product = await (await fetch(`${root}products/${handle}.js`)).json();
          (product.variants || []).forEach((variant) => {
            this.compareByVariant[variant.id] = variant.compare_at_price || 0;
          });
        } catch (_) {}
      })
    );
  }

  onInput(e) {
    const t = e.target;
    if (t.matches('[data-item-pct]')) {
      const val = parseFloat(t.value);
      if (Number.isFinite(val)) this.overrides[t.dataset.itemPct] = val;
      else delete this.overrides[t.dataset.itemPct];
    } else if (t.matches('[data-item-venta]')) {
      this.aplicarPrecioVenta(t);
    } else if (t.matches('[data-item-qty]')) {
      this.fijarCantidad(t.dataset.itemQty, t.value);
      return;
    } else if (!t.matches('[data-global-pct]') && !t.matches('[data-costos]')) {
      return;
    }
    this.renderTotales();
    this.renderLineas(false);
    this.guardarEstado();
  }

  fijarCantidad(key, valor) {
    const cantidad = Math.max(0, parseInt(valor, 10) || 0);
    this.cantidades = this.cantidades || {};
    this.cantidades[key] = cantidad;
    this.renderTotales();
    this.renderLineas(false);
  }

  cantidadLocal(item) {
    const guardada = this.cantidades?.[item.key];
    return Number.isFinite(guardada) ? guardada : item.quantity || 1;
  }

  async actualizarCarrito(boton) {
    const estado = this.querySelector('[data-carrito-estado]');
    const updates = {};
    this.querySelectorAll('[data-item-qty]').forEach((input) => {
      const key = input.dataset.itemQty;
      const cantidad = Math.max(0, parseInt(input.value, 10) || 0);
      const item = (this.cart?.items || []).find((linea) => linea.key === key);
      if (item && cantidad !== item.quantity) updates[key] = cantidad;
    });

    if (!Object.keys(updates).length) {
      if (estado) {
        estado.hidden = false;
        estado.textContent = 'El carrito ya tiene esas cantidades.';
      }
      return;
    }

    if (boton) boton.disabled = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const cart = await res.json();
      if (!res.ok || cart?.errors || cart?.status >= 400) {
        if (estado) {
          estado.hidden = false;
          estado.textContent = 'No se pudo actualizar el carrito.';
        }
        return;
      }
      this.cantidades = {};
      this.cart = cart;
      this.render();
      if (window.mjCartAvailability?.syncUI) await window.mjCartAvailability.syncUI(cart);
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'calculadora-rentabilidad',
          cartData: cart,
        });
      }
      const aviso = this.querySelector('[data-carrito-estado]');
      if (aviso) {
        aviso.hidden = false;
        aviso.textContent = 'Carrito actualizado.';
      }
    } catch (err) {
      if (estado) {
        estado.hidden = false;
        estado.textContent = 'No se pudo actualizar el carrito.';
      }
      console.error('[Calculadora rentabilidad] No se pudo actualizar el carrito', err);
    } finally {
      if (boton) boton.disabled = false;
    }
  }

  factorNeto() {
    return this.neto ? 1 + this.iva / 100 : 1;
  }

  aplicarPrecioVenta(input) {
    if (input.disabled || this.markupMode !== 'manual') return;
    const key = input.dataset.itemVenta;
    const unitVenta = parseFloat(input.value);
    this.ventasManual = this.ventasManual || {};
    if (Number.isFinite(unitVenta) && unitVenta >= 0) this.ventasManual[key] = unitVenta;
    else delete this.ventasManual[key];
  }

  congelarVentas() {
    const { lineas } = this.calcular();
    this.ventasManual = {};
    lineas.forEach((l) => {
      const unit = Number.isFinite(l.unitVenta)
        ? l.unitVenta
        : (l.qty > 0 ? l.venta / l.qty : (l.unitPago || 0) * (1 + (l.pct || 0) / 100));
      this.ventasManual[l.item.key] = unit;
    });
  }

  pctGlobal() {
    const v = this.globalInput ? parseFloat(this.globalInput.value) : this.defaultPct;
    return Number.isFinite(v) ? v : this.defaultPct;
  }

  pctItem(key) {
    return Number.isFinite(this.overrides[key]) ? this.overrides[key] : this.pctGlobal();
  }

  descuento(item, factorNeto) {
    const qty = item.quantity || 1;
    const finalLine = item.final_line_price || 0;
    const originalLine = item.original_line_price || finalLine;
    const compareAt = this.compareByVariant[item.variant_id] || item.compare_at_price || 0;
    const compareLine = compareAt > 0 ? compareAt * qty : 0;
    const wasLine = Math.max(originalLine, compareLine);
    const onSale = wasLine > finalLine;
    const percent = onSale ? Math.round(((wasLine - finalLine) / wasLine) * 100) : 0;
    return { onSale, percent, costoOriginal: wasLine / 100 / factorNeto };
  }

  calcular() {
    const items = this.itemsFacturables();
    const factorNeto = this.factorNeto();
    const costosExtra = this.costosExtra();

    const factor = this.factorTramo();
    const lineas = items.map((item) => {
      const qtyCart = item.quantity || 1;
      const qty = this.cantidadLocal(item);
      const costoCart = item.final_line_price / 100 / factorNeto;
      const unitLista = qtyCart > 0 ? costoCart / qtyCart : 0;
      const unitPago = unitLista * factor;
      const costoLista = unitLista * qty;
      const costo = unitPago * qty;
      const pct = this.pctItem(item.key);
      const desc = this.descuento(item, factorNeto);
      const costoOriginal = qtyCart > 0 ? (desc.costoOriginal / qtyCart) * qty : 0;
      let unitVenta = unitPago * (1 + pct / 100);
      if (this.markupMode === 'manual') {
        this.ventasManual = this.ventasManual || {};
        if (!Number.isFinite(this.ventasManual[item.key])) this.ventasManual[item.key] = unitVenta;
        unitVenta = this.ventasManual[item.key];
      }
      return { item, qty, costo, costoLista, unitPago, unitVenta, pct, venta: unitVenta * qty, ...desc, costoOriginal };
    });

    const joyasLista = lineas.reduce((s, l) => s + l.costoLista, 0);
    const tramo = this.tramosActivos() ? this.tramoDe(joyasLista) : null;
    const tramoPct = tramo ? tramo.d : 0;
    lineas.forEach((l) => { l.tramoPct = tramoPct; });
    const inversion = lineas.reduce((s, l) => s + l.costo, 0) + costosExtra;
    const venta = lineas.reduce((s, l) => s + l.venta, 0);
    const ganancia = venta - inversion;
    const margen = venta > 0 ? ganancia / venta : 0;
    const rentabilidad = inversion > 0 ? ganancia / inversion : 0;

    return { lineas, inversion, venta, ganancia, margen, rentabilidad, costos: costosExtra, joyasLista };
  }

  tramosActivos() {
    return this.dataset.tramos !== 'false';
  }

  factorTramo() {
    if (!this.tramosActivos()) return 1;
    const factorNeto = this.factorNeto();
    const joyasLista = this.itemsFacturables().reduce((suma, item) => {
      const qtyCart = item.quantity || 1;
      const qty = this.cantidadLocal(item);
      const costoCart = item.final_line_price / 100 / factorNeto;
      const unitLista = qtyCart > 0 ? costoCart / qtyCart : 0;
      return suma + unitLista * qty;
    }, 0);
    const tramo = this.tramoDe(joyasLista);
    return tramo ? 1 - tramo.d / 100 : 1;
  }

  tramos() {
    const monto = (key, fallback) => {
      const valor = parseFloat(this.dataset[key]);
      return Number.isFinite(valor) && valor > 0 ? valor : fallback;
    };
    return [
      { min: monto('tramo10', 150000), d: 10 },
      { min: monto('tramo15', 400000), d: 15 },
      { min: monto('tramo20', 1000000), d: 20 },
    ];
  }

  tramoDe(sub) {
    return [...this.tramos()].reverse().find((tramo) => sub >= tramo.min) || null;
  }

  money(v) {
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.cart?.currency || 'CLP',
    }).format(v);
  }

  precioEntero(v) {
    return (this.cart?.currency || 'CLP') === 'CLP' ? String(Math.round(v)) : String(Math.round(v * 100) / 100);
  }

  pct(v) {
    return new Intl.NumberFormat(this.locale, { style: 'percent', maximumFractionDigits: 1 }).format(v);
  }

  esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  render() {
    const vacio = this.itemsFacturables().length === 0;
    if (this.fab) this.fab.hidden = false;
    const footVacio = this.querySelector('[data-foot-vacio]');
    const footLleno = this.querySelector('[data-foot-lleno]');
    if (footVacio) footVacio.hidden = !vacio;
    if (footLleno) footLleno.hidden = vacio;
    if (this.emptyEl) this.emptyEl.hidden = !vacio;
    if (this.bodyEl) this.bodyEl.hidden = vacio;
    if (vacio) {
      this.renderVacio();
      this.pintarFab(null);
      return;
    }
    this.renderLineas(true);
    this.renderTotales();
  }

  renderVacio() {
    if (!this.emptyEl) return;
    const montos = [39990, 100000, 150000, 200000, 400000];
    const inv = this.sim || 0;
    const gana = inv;
    const vendes = inv * 2;
    this.emptyEl.innerHTML = `
      <div class="calc-v2__blk">
        <h3>¿Cuánto quieres invertir?</h3>
        <div class="calc-v2__chips">
          ${montos.map((v) => `<button type="button" data-sim="${v}" aria-pressed="${v === inv}">${this.money(v)}</button>`).join('')}
        </div>
      </div>
      <div class="calc-v2__res">
        <small>Si vendes tus joyas al doble, tu ganancia sería</small>
        <span class="calc-v2__big">${this.money(gana)}</span>
        <div class="calc-v2__rows"><div><span>${this.esc(this.dataset.labelPagas || 'Pagas')}</span><strong>${this.money(inv)}</strong></div><div><span>${this.esc(this.dataset.labelVendes || 'Vendes en')}</span><strong>${this.money(vendes)}</strong></div></div>
      </div>
      <div class="calc-v2__learn"><span class="calc-v2__bulb">i</span><span>Vender al doble significa que cada joya te devuelve lo que pagaste <strong>y lo mismo de ganancia</strong>.</span></div>
      <p class="calc-v2__helper">Cuando agregues joyas al carro, aquí verás el cálculo exacto pieza por pieza.</p>`;
    const sub = this.querySelector('[data-sub]');
    if (sub) sub.textContent = 'Tu carro está vacío. Simula un monto y mira cuánto podrías ganar.';
  }

  margenOptimo() {
    return 50;
  }

  aplicarEstadoMargen(row, pct) {
    const bajo = pct < this.margenOptimo();
    const hint = row.querySelector('[data-margen-hint]');
    if (hint) {
      hint.textContent = bajo ? 'margen muy bajo' : 'margen recomendado';
      hint.classList.toggle('tw-text-[#d32f2f]', bajo);
      hint.classList.toggle('tw-text-[#1b7a6e]', !bajo);
    }
    const input = row.querySelector('[data-item-pct]');
    if (input) {
      input.classList.toggle('tw-border-[#d32f2f]', bajo);
      input.classList.toggle('tw-text-[#d32f2f]', bajo);
    }
  }

  lineaCard(l) {
    const qty = l.qty;
    const unit = l.unitPago || 0;
    const unitVenta = Number.isFinite(l.unitVenta) ? l.unitVenta : (qty > 0 ? l.venta / qty : unit * (1 + l.pct / 100));
    const bloqueado = this.markupMode !== 'manual';
    const ganancia = (unitVenta - unit) * qty;
    const ratio = unit > 0 ? (unitVenta - unit) / unit : 0;
    const gainClass = ganancia < 0 ? 'is-neg' : ratio < 0.3 ? 'is-low' : '';
    const hint = qty === 0
      ? `<p class="calc-v2__hint is-low">Se quitará de tu carro al apretar «${this.esc(this.dataset.textoActualizar || 'Actualizar carro')}».</p>`
      : ganancia < 0
        ? '<p class="calc-v2__hint is-neg">Así pierdes plata con esta joya: súbele el precio.</p>'
        : ratio < 0.3
          ? '<p class="calc-v2__hint is-low">Ganancia baja: prueba subir un poco el precio.</p>'
          : '';
    const img = l.item.image
      ? `<img src="${this.esc(l.item.image)}&width=120" alt="" width="56" height="56" loading="lazy">`
      : '<span class="calc-v2__ph"></span>';
    const tieneTramo = l.tramoPct > 0 && l.costoLista - l.costo > 0.5;
    const tramo = `<span class="calc-v2__tramo" data-tramo ${tieneTramo ? '' : 'hidden'}>${tieneTramo ? `−${l.tramoPct}% por tramo` : ''}</span>`;
    return `<article class="calc-v2__item" data-row="${this.esc(l.item.key)}">
      ${img}
      <div class="calc-v2__nm"><b>${this.esc(l.item.product_title)}</b>${l.item.variant_title ? `<small>${this.esc(l.item.variant_title)}</small>` : ''}</div>
      <span class="calc-v2__step">
        <button type="button" data-qty-step="-1" aria-label="Quitar una">−</button>
        <input type="number" inputmode="numeric" min="0" data-item-qty="${this.esc(l.item.key)}" value="${qty}" aria-label="Cantidad">
        <button type="button" data-qty-step="1" aria-label="Agregar una">+</button>
      </span>
      <div class="calc-v2__nums">
        <div><span>${this.esc(this.dataset.labelPagas || 'Pagas')}</span><strong data-pay>${this.money(l.costo)}</strong><s data-pay-lista ${l.costoLista - l.costo > 0.5 ? '' : 'hidden'}>${this.money(l.costoLista)}</s>${tramo}<small data-pay-detail>${qty} × ${this.money(unit)}</small></div>
        <div class="calc-v2__sell"><span>La vendes a (c/u)</span><span class="calc-v2__money${bloqueado ? ' is-locked' : ''}"><em>$</em><input type="number" inputmode="numeric" min="0" step="10" data-item-venta="${this.esc(l.item.key)}" value="${this.precioEntero(unitVenta)}"${bloqueado ? ' disabled' : ''}></span><small data-venta-total>Total ${this.money(unitVenta * qty)}</small></div>
        <div class="calc-v2__gain ${gainClass}"><span>${this.esc(this.dataset.labelGanancia || 'Tu ganancia')}</span><strong data-gain>${this.money(ganancia)}</strong><small data-gain-unit>${this.money(unitVenta - unit)} c/u</small></div>
      </div>
      ${hint}
    </article>`;
  }

  renderLineas(rebuild) {
    const { lineas } = this.calcular();
    if (!this.itemsEl) return;
    if (!rebuild) {
      lineas.forEach((l) => {
        const row = this.itemsEl.querySelector(`[data-row="${CSS.escape(l.item.key)}"]`);
        if (!row) return;
        const qty = l.qty;
        const unit = l.unitPago || 0;
        const unitVenta = Number.isFinite(l.unitVenta) ? l.unitVenta : (qty > 0 ? l.venta / qty : unit * (1 + l.pct / 100));
        const pay = row.querySelector('[data-pay]');
        if (pay) pay.textContent = this.money(l.costo);
        const payLista = row.querySelector('[data-pay-lista]');
        if (payLista) {
          payLista.hidden = l.costoLista - l.costo <= 0.5;
          payLista.textContent = this.money(l.costoLista);
        }
        const tramo = row.querySelector('[data-tramo]');
        if (tramo) {
          const tieneTramo = l.tramoPct > 0 && l.costoLista - l.costo > 0.5;
          tramo.hidden = !tieneTramo;
          tramo.textContent = tieneTramo ? `−${l.tramoPct}% por tramo` : '';
        }
        const detail = row.querySelector('[data-pay-detail]');
        if (detail) detail.textContent = `${qty} × ${this.money(unit)}`;
        const gain = row.querySelector('[data-gain]');
        if (gain) gain.textContent = this.money((unitVenta - unit) * qty);
        const gainUnit = row.querySelector('[data-gain-unit]');
        if (gainUnit) gainUnit.textContent = `${this.money(unitVenta - unit)} c/u`;
        const ventaInput = row.querySelector('[data-item-venta]');
        if (ventaInput) {
          const bloqueado = this.markupMode !== 'manual';
          ventaInput.disabled = bloqueado;
          ventaInput.closest('.calc-v2__money')?.classList.toggle('is-locked', bloqueado);
          if (document.activeElement !== ventaInput) ventaInput.value = this.precioEntero(unitVenta);
        }
        const ventaTotal = row.querySelector('[data-venta-total]');
        if (ventaTotal) ventaTotal.textContent = `Total ${this.money(unitVenta * qty)}`;
      });
      return;
    }
    this.itemsEl.innerHTML = lineas.map((l) => this.lineaCard(l)).join('');
  }

  pintarFab(r) {
    const label = this.querySelector('[data-fab-label]');
    if (!this.fabValor && this.fab) this.fabValor = this.querySelector('[data-fab-valor]');
    if (!r) {
      if (label) label.textContent = '¿Cuánto puedes ganar?';
      if (this.fabValor) {
        this.fabValor.textContent = 'Calcula tu ganancia';
        this.fabValor.classList.remove('is-amount');
      }
      return;
    }
    if (label) label.textContent = this.dataset.labelGanancia || 'Tu ganancia';
    if (this.fabValor) {
      this.fabValor.textContent = this.money(r.ganancia);
      this.fabValor.classList.add('is-amount');
    }
  }

  renderTotales() {
    const r = this.calcular();
    const set = (k, v) => {
      this.querySelectorAll(`[data-total="${k}"]`).forEach((el) => { el.textContent = v; });
    };
    set('inversion', this.money(r.inversion));
    set('venta', this.money(r.venta));
    set('ganancia', this.money(r.ganancia));
    set('ganancia-stick', this.money(r.ganancia));
    set('margen', this.pct(r.margen));
    set('rentabilidad', this.pct(r.rentabilidad));
    set('costos', this.money(r.costos || 0));
    const pagasLista = (r.joyasLista || 0) + (r.costos || 0);
    const inversionLista = this.querySelector('[data-total="inversion-lista"]');
    if (inversionLista) {
      inversionLista.hidden = pagasLista - r.inversion <= 0.5;
      inversionLista.textContent = this.money(pagasLista);
    }
    const big = this.querySelector('[data-total="ganancia"]');
    if (big) big.classList.toggle('is-neg', r.ganancia < 0);
    const stickSub = this.querySelector('[data-stick-sub]');
    if (stickSub) stickSub.textContent = `${this.dataset.labelPagas || 'Pagas'} ${this.money(r.inversion)} · ${this.dataset.labelVendes || 'Vendes en'} ${this.money(r.venta)}`;
    const learn = this.querySelector('[data-learn]');
    if (learn) {
      const avisoPerdida = this.querySelector('[data-mode="100"]')
        ? 'Ojo: con estos precios <strong>pierdes plata</strong>. Prueba "Al doble" o revisa tus gastos.'
        : 'Ojo: con estos precios <strong>pierdes plata</strong>. Súbele el precio o revisa tus gastos.';
      learn.innerHTML = r.ganancia < 0
        ? avisoPerdida
        : `Por cada <strong>$1.000</strong> que inviertes, recuperas tus $1.000 y ganas <strong>${this.money(Math.round(r.rentabilidad * 1000))}</strong> más.`;
    }
    const n = r.lineas.reduce((s, l) => s + l.qty, 0);
    const sub = this.querySelector('[data-sub]');
    const pendiente = r.lineas.some((l) => l.qty !== (l.item.quantity || 1));
    if (sub) sub.textContent = pendiente
      ? `Calculada con ${n} joyas (con tus cambios sin guardar).`
      : `Calculada con las ${n} joyas de tu carro.`;
    const pend = this.querySelector('[data-pend]');
    if (pend) pend.hidden = !pendiente;
    const joyas = r.joyasLista;
    const nudge = this.querySelector('[data-nudge]');
    if (nudge) nudge.innerHTML = this.htmlNudge(joyas);
    const margenTxt = this.querySelector('[data-margen-txt]');
    if (margenTxt) margenTxt.textContent = `De cada $100 que vendes, ${this.money(Math.max(0, r.margen * 100))} son tuyos.`;
    const rentTxt = this.querySelector('[data-rent-txt]');
    if (rentTxt) rentTxt.textContent = `Tu plata crece ${this.pct(Math.max(0, r.rentabilidad))}.`;
    this.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === this.markupMode)));
    const helper = this.querySelector('[data-mode-helper]');
    if (helper) {
      if (this.markupMode === 'manual') {
        const sugerencia = this.querySelector('[data-sugerencia]');
        const nombre = sugerencia?.textContent?.trim() || 'un atajo';
        helper.textContent = sugerencia
          ? `Estás poniendo tus propios precios. Toca «${nombre}» para volver a aplicarlo a todas.`
          : 'Estás poniendo tus propios precios. Toca un atajo para volver a aplicarlo a todas.';
      }
      else {
        const venta = Math.round(10000 * (1 + this.defaultPct / 100));
        helper.innerHTML = `El precio de venta es lo que pagas más lo que le sumas. Ejemplo: lo que pagas a <strong>$10.000</strong>, lo vendes a <strong>$${venta.toLocaleString('es-CL')}</strong>.`;
      }
    }
    this.pintarFab(r);
    this.vigilarResultado();
  }

  htmlNudge(sub) {
    const min = 39990;
    const tramos = this.tramos();
    if (sub < min) {
      return `<div class="calc-v2__nudge is-warn"><span>Te faltan <b>${this.money(min - sub)}</b> para la compra mínima de $39.990.</span><span class="bar"><i style="width:${Math.min(100, (sub / min) * 100)}%"></i></span></div>`;
    }
    const next = tramos.find((t) => sub < t.min);
    const actual = [...tramos].reverse().find((t) => sub >= t.min);
    const ahora = actual ? `Tu pedido ya está en el tramo de <b>${actual.d}% menos</b>. ` : '';
    if (next) {
      return `<div class="calc-v2__nudge is-go"><span>${ahora}Te faltan <b>${this.money(next.min - sub)}</b> para que todo tu pedido baje ${next.d}%.</span><span class="bar"><i style="width:${Math.min(100, (sub / next.min) * 100)}%"></i></span></div>`;
    }
    return `<div class="calc-v2__nudge is-go"><span>${ahora}Ya tienes el mayor descuento por volumen.</span></div>`;
  }

  vigilarResultado() {
    const res = this.querySelector('[data-res]');
    const stick = this.querySelector('[data-stick]');
    const root = this.querySelector('[data-scroll]');
    if (!res || !stick || !root || !('IntersectionObserver' in window)) return;
    if (this._io) this._io.disconnect();
    this._io = new IntersectionObserver(([e]) => stick.classList.toggle('on', !e.isIntersecting), { root, threshold: 0 });
    this._io.observe(res);
  }
}

if (!customElements.get('calculadora-rentabilidad')) {
  customElements.define('calculadora-rentabilidad', CalculadoraRentabilidad);
}

// 

/* leftover
const rowClass =
      'max-md:tw-mb-3 max-md:tw-grid max-md:tw-grid-cols-2 max-md:tw-gap-x-3 max-md:tw-gap-y-3 max-md:tw-rounded-mj max-md:tw-border max-md:tw-border-solid max-md:tw-border-mj-line max-md:tw-p-3';
    const prodClass =
      'tw-flex tw-items-center tw-gap-2.5 tw-border-b tw-border-solid tw-border-mj-line tw-px-2 tw-py-2.5 tw-align-middle max-md:tw-col-span-2 max-md:tw-block max-md:tw-border-0 max-md:tw-p-0';
    const cellClass =
      'tw-border-b tw-border-solid tw-border-mj-line tw-px-2 tw-py-2.5 tw-text-right tw-align-middle max-md:tw-block max-md:tw-border-0 max-md:tw-p-0 max-md:tw-text-left max-md:before:tw-mb-1 max-md:before:tw-block max-md:before:tw-text-[11px] max-md:before:tw-font-semibold max-md:before:tw-uppercase max-md:before:tw-tracking-wide max-md:before:tw-opacity-55 max-md:before:tw-content-[attr(data-label)]';
    const priceInputClass =
      'tw-w-[110px] tw-min-w-0 tw-border-0 tw-bg-transparent tw-py-1.5 tw-text-right tw-text-[1.05em] tw-text-inherit tw-outline-none';
    const badgeClass =
      'tw-inline-flex tw-min-h-5 tw-shrink-0 tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-mj tw-bg-mj-pink tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-leading-4 tw-text-white';

    this.itemsEl.innerHTML = lineas
      .map((l) => {
        const img = l.item.image
          ? `<img class="tw-h-12 tw-w-12 tw-rounded-mj tw-object-cover" src="${this.esc(l.item.image)}&width=96" alt="" width="48" height="48" loading="lazy">`
          : '';
        const bajo = l.pct < this.margenOptimo();
        const hintClass = bajo ? 'tw-text-[#d32f2f]' : 'tw-text-[#1b7a6e]';
        const hintText = bajo ? 'margen muy bajo' : 'margen recomendado';
        const pctCell = `<div class="tw-inline-flex tw-flex-col tw-items-end tw-gap-1 max-md:tw-items-start">
          <span data-item-margen>${Math.round(l.pct * 10) / 10}%</span>
          <small class="tw-text-[11px] tw-font-semibold tw-leading-tight ${hintClass}" data-margen-hint>${hintText}</small>
        </div>`;
        const badge = l.onSale
          ? `<span class="${badgeClass}" title="${this.esc(this.saleLabel)}">-${l.percent}%</span>`
          : '';
        const qty = l.qty;
        const unitActual = l.costo / qty;
        const unitOriginal = l.costoOriginal / qty;
        const precioHtml = (actual, original) =>
          l.onSale
            ? `<s class="tw-mr-1 tw-text-mj-ink/50 tw-no-underline tw-line-through">${this.money(original)}</s><span class="tw-font-semibold tw-text-mj-pink">${this.money(actual)}</span>`
            : this.money(actual);
        const ventaHint = qty > 1 ? `precio a vender por unidad · total ${this.money(l.venta)}` : 'precio a vender por unidad';
        const ventaCell = `<div class="tw-inline-flex tw-flex-col tw-items-end tw-gap-1 max-md:tw-w-full max-md:tw-items-start">
          <span class="tw-inline-flex tw-w-full tw-max-w-[140px] tw-items-center tw-gap-1 tw-rounded-mj tw-border tw-border-solid tw-border-mj-line tw-px-2 max-md:tw-max-w-none">
            <em class="tw-not-italic tw-opacity-60">$</em>
            <input type="number" inputmode="numeric" min="0" step="1" class="${priceInputClass} max-md:tw-w-full max-md:tw-text-left"
              data-item-venta="${this.esc(l.item.key)}" value="${this.precioEntero(qty > 0 ? l.venta / qty : 0)}"
              aria-label="Precio a vender por unidad de ${this.esc(l.item.product_title)}">
          </span>
          <small class="tw-text-[11px] tw-leading-tight tw-text-mj-ink/55" data-venta-total>${ventaHint}</small>
        </div>`;
        return `
          <tr class="${rowClass}" data-row="${this.esc(l.item.key)}">
            <td class="${prodClass}">${img}<div>
              <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-1"><strong>${this.esc(l.item.product_title)}</strong>${badge}</div>
              ${l.item.variant_title ? `<small class="tw-block tw-opacity-65">${this.esc(l.item.variant_title)}</small>` : ''}
            </div></td>
            <td class="${cellClass}" data-label="Cantidad">
              <span class="tw-inline-flex tw-items-center tw-overflow-hidden tw-rounded-mj tw-border tw-border-solid tw-border-mj-line">
                <button type="button" class="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-border-0 tw-bg-transparent tw-text-base tw-leading-none" data-qty-step="-1" aria-label="Disminuir cantidad de ${this.esc(l.item.product_title)}">−</button>
                <input type="number" inputmode="numeric" min="0" step="1"
                  class="tw-w-10 tw-border-0 tw-bg-transparent tw-p-0 tw-text-center tw-text-inherit tw-outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:tw-appearance-none [&::-webkit-outer-spin-button]:tw-appearance-none"
                  data-item-qty="${this.esc(l.item.key)}" value="${qty}"
                  aria-label="Cantidad de ${this.esc(l.item.product_title)}">
                <button type="button" class="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-border-0 tw-bg-transparent tw-text-base tw-leading-none" data-qty-step="1" aria-label="Aumentar cantidad de ${this.esc(l.item.product_title)}">+</button>
              </span>
            </td>
            <td class="${cellClass}" data-label="Costo por unidad">${precioHtml(unitActual, unitOriginal)}</td>
            <td class="${cellClass}" data-label="Costo total">${precioHtml(l.costo, l.costoOriginal)}</td>
            <td class="${cellClass}" data-label="% de ganancia (margen)">${pctCell}</td>
            <td class="${cellClass} max-md:tw-col-span-2" data-label="Precio a vender por unidad">${ventaCell}</td>
          </tr>`;
      })
      .join('');
  }

  renderTotales() {
    const r = this.calcular();
    const set = (k, v) => {
      const el = this.querySelector(`[data-total="${k}"]`);
      if (el) el.textContent = v;
    };
    set('inversion', this.money(r.inversion));
    set('venta', this.money(r.venta));
    set('ganancia', this.money(r.ganancia));
    set('margen', this.pct(r.margen));
    set('rentabilidad', this.pct(r.rentabilidad));
    set('costos', this.money(r.costos || 0));

    const g = this.querySelector('[data-total="ganancia"]');
    if (g) g.classList.toggle('tw-text-[#ffb4a8]', r.ganancia < 0);

*/

